import { format } from "date-fns";
import { AlertTriangle, XIcon } from "lucide-react";
import { useFieldArray, useFormContext } from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

import { AddSessionFormData } from "../types/add-session-schema";

export function RoomSystemsListElement() {
  const form = useFormContext<AddSessionFormData>();
  const roomSystems = useFieldArray({
    control: form.control,
    name: "roomSystems",
  });

  if (roomSystems.fields.length === 0) {
    return null;
  }

  const roomSystemsWithConflicts = roomSystems.fields.filter(
    (roomSystem) => roomSystem.conflictingSessions.length > 0,
  );

  return (
    <>
      <ScrollArea
        type="always"
        className={cn("rounded-md border border-border", {
          "h-[427px]": roomSystems.fields.length > 7, // 7 items * 61px = 427px
        })}
      >
        <div className="divide-y divide-border">
          {roomSystems.fields.map((roomSystem, index) => (
            <div
              key={roomSystem.id}
              className="flex items-center justify-between px-4 py-2"
            >
              <div className="flex min-w-0 items-center">
                <div className="ml-2 min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {roomSystem.name}
                  </p>
                  <p className="truncate text-sm text-muted-foreground">
                    {roomSystem.organisationName}
                  </p>
                </div>
              </div>

              {roomSystem.conflictingSessions.length > 0 && (
                <Badge variant="outline">
                  <span className="flex items-center">
                    <span className="mr-2 inline-block size-2 rounded-full bg-warning" />
                    Scheduling conflict
                  </span>
                </Badge>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={() => roomSystems.remove(index)}
                type="button"
              >
                <XIcon className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>
      {roomSystemsWithConflicts.length > 0 && (
        <Alert className="mt-4 p-2">
          <div className="border-l-2 border-warning p-2">
            <AlertTitle className="flex items-center text-sm text-warning">
              <AlertTriangle className="mr-2 h-4 w-4" />
              Room System Conflicts
            </AlertTitle>
            <AlertDescription>
              {roomSystemsWithConflicts.map((roomSystem) => (
                <p
                  key={roomSystem.id}
                  className="mt-4 space-y-2 text-sm leading-none"
                >
                  <span className="block leading-none">
                    {roomSystem.name} - {roomSystem.organisationName}
                  </span>
                  {roomSystem.conflictingSessions.map((session) => {
                    const sessionDuration = `${format(
                      session.start,
                      "HH:mm",
                    )} - ${format(session.end, "HH:mm")}`;

                    return (
                      <span key={session.id} className="block leading-none">
                        <span className="font-medium">{session.name}</span>{" "}
                        {sessionDuration}
                      </span>
                    );
                  })}
                </p>
              ))}
            </AlertDescription>
          </div>
        </Alert>
      )}
    </>
  );
}
